import React from "react";

export default function ConflictModal({
  open,
  data,
  onClose,
  onRetry,
  onContinueAnyway,
  onPatchPayload,
}) {
  if (!open) return null;

  const blocking = data?.blocking || null;
  const warning = data?.warning || null;
  const conflicts = Array.isArray(data?.conflicts) ? data.conflicts : [];
  const payload = data?.payload || {};

  const headerClass = blocking ? "bg-red-600 text-white" : "bg-yellow-300 text-black";

  const addMinutes = (hm, delta) => {
    const m = String(hm || "").match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return hm;
    let h = Number(m[1]), mi = Number(m[2]);
    let t = h * 60 + mi + delta;
    if (t < 0) t = 0;
    if (t > 24 * 60 - 1) t = 24 * 60 - 1;
    const hh = String(Math.floor(t / 60)).padStart(2, "0");
    const mm = String(t % 60).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className={`px-4 py-3 ${headerClass}`}>
          <div className="text-lg font-extrabold">
            {blocking ? "Nelze uložit – konflikt" : "Pozor – možný konflikt"}
          </div>
          <div className="mt-1 text-sm opacity-90">
            {blocking || warning || "Kolize v kalendáři"}
          </div>
        </div>

        <div className="p-4">
          {/* Rychlá oprava */}
          <div className="rounded-2xl bg-gray-50 p-3">
            <div className="mb-2 font-extrabold">Rychlá oprava</div>

            <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
              <div className="rounded-xl border bg-white p-2">
                <div className="mb-2 text-xs font-bold">Čas</div>
                <div className="flex flex-wrap gap-2">
                  {[-30, -15, 15, 30].map((m) => (
                    <button
                      key={m}
                      className="rounded-xl border px-3 py-2 text-sm font-bold"
                      onClick={() => {
                        onPatchPayload?.({
                          casOd: addMinutes(payload.casOd, m),
                          casDo: addMinutes(payload.casDo, m),
                        });
                      }}
                      title={`Posunout o ${m} minut`}
                    >
                      {m > 0 ? `+${m}` : m}m
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border bg-white p-2">
                <div className="mb-2 text-xs font-bold">Posádka</div>
                <input
                  className="w-full rounded-xl border px-3 py-2"
                  value={payload.posadkaNazev || ""}
                  placeholder="např. Stanko + Novák"
                  onChange={(e) => onPatchPayload?.({ posadkaNazev: e.target.value })}
                />
              </div>

              <div className="rounded-xl border bg-white p-2">
                <div className="mb-2 text-xs font-bold">Auto (SPZ)</div>
                <input
                  className="w-full rounded-xl border px-3 py-2"
                  value={payload.autoSpz || ""}
                  placeholder="např. 1AB2345"
                  onChange={(e) => onPatchPayload?.({ autoSpz: e.target.value })}
                />
              </div>

              <div className="rounded-xl border bg-white p-2">
                <div className="mb-2 text-xs font-bold">Místo</div>
                <input
                  className="w-full rounded-xl border px-3 py-2"
                  value={payload.misto || ""}
                  placeholder="např. Obřadní síň Český Krumlov"
                  onChange={(e) => onPatchPayload?.({ misto: e.target.value })}
                />
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                className="rounded-xl border px-3 py-2 text-sm font-extrabold"
                onClick={onRetry}
              >
                Zkontrolovat znovu
              </button>

              <div className="text-xs opacity-70">
                Uprav pole → “Zkontrolovat znovu” → pak znovu Uložit.
              </div>
            </div>
          </div>

          {/* Konflikty */}
          <div className="mt-4">
            <div className="mb-2 font-extrabold">Nalezené konflikty ({conflicts.length})</div>

            {conflicts.length === 0 ? (
              <div className="rounded-xl bg-gray-50 p-3 text-sm">
                Žádné detailní konflikty k vypsání.
              </div>
            ) : (
              <div className="space-y-2">
                {conflicts.map((c, i) => (
                  <div key={c.withId || c.id || i} className="rounded-2xl border p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-xl bg-gray-100 px-2 py-1 text-xs font-extrabold">
                        {c.reason || "KOLIZE"}
                      </span>
                      <span className="text-sm font-bold">
                        {(c.datum || "")} {(c.casOd || "")}–{(c.casDo || "")}
                      </span>
                    </div>
                    <div className="mt-1 text-sm opacity-90">
                      {c.misto ? <div><b>Místo:</b> {c.misto}</div> : null}
                      {c.zesnuly ? <div><b>Zesnulý:</b> {c.zesnuly}</div> : null}
                      {c.posadka ? <div><b>Posádka:</b> {c.posadka}</div> : null}
                      {c.spz ? <div><b>Auto:</b> {c.spz}</div> : null}
                      {c.poznamka ? <div className="text-xs opacity-75">{c.poznamka}</div> : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 bg-gray-50 p-4 sm:flex-row sm:justify-end">
          <button className="rounded-xl border px-4 py-2 font-extrabold" onClick={onClose}>
            Zavřít
          </button>

          {!blocking && warning ? (
            <button
              className="rounded-xl bg-black px-4 py-2 font-extrabold text-white"
              onClick={onContinueAnyway}
              title="Uloží i přes varování"
            >
              Uložit i tak
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
